import React, { createContext, useContext, useEffect, useRef, ReactNode } from 'react';
import { useEvent } from './EventContext';
import { realtimeService, ChangementDistant } from '../services/realtime.service';

/**
 * Abonnement temps réel de cloud-admin (LOG-201).
 *
 * La room est celle de l'organisation de l'ÉVÉNEMENT sélectionné, jamais celle
 * de l'utilisateur connecté : un admin qui assiste un client est précisément le
 * cas où les deux diffèrent — ce piège nous a déjà coûté deux incidents
 * (2026-07-05 sur la sync, 2026-08-16 sur la messagerie).
 *
 * Changer d'événement dans le sélecteur réabonne, et la connexion se referme
 * quand il n'y en a plus.
 */

interface RealtimeContextType {
  /** Rappelle `handler` pour les seuls types d'entités demandés. */
  abonner: (types: string[], handler: (c: ChangementDistant) => void) => () => void;
}

const RealtimeContext = createContext<RealtimeContextType | undefined>(undefined);

export const RealtimeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { selectedEvent } = useEvent();
  const orgId = selectedEvent?.organization_id ?? null;

  useEffect(() => {
    if (!orgId) {
      realtimeService.deconnecter();
      return;
    }
    realtimeService.connecter(orgId);
    return () => realtimeService.deconnecter();
  }, [orgId]);

  const abonner = React.useCallback(
    (types: string[], handler: (c: ChangementDistant) => void) =>
      realtimeService.ecouter((changement) => {
        if (types.length === 0 || types.includes(changement.entity_type)) {
          handler(changement);
        }
      }),
    []
  );

  return (
    <RealtimeContext.Provider value={{ abonner }}>{children}</RealtimeContext.Provider>
  );
};

/**
 * Rejoue `onChange` quand une entité surveillée bouge côté serveur.
 *
 * On n'écoute QUE ce qui concerne l'écran : sans ce filtre, la liste des heats
 * se rechargerait à chaque modification d'équipe. C'est le défaut que
 * `ScoresProvider` a évité côté Flutter (2026-08-16), pour la même raison.
 *
 * `onChange` est gardée dans une ref : passer une fonction anonyme depuis un
 * écran ne doit pas se désabonner et se réabonner à chaque rendu.
 */
export function useRealtime(types: string[], onChange: (c: ChangementDistant) => void) {
  const ctx = useContext(RealtimeContext);
  const rappel = useRef(onChange);
  rappel.current = onChange;

  const cle = types.join(',');
  useEffect(() => {
    if (!ctx) return;
    return ctx.abonner(cle ? cle.split(',') : [], (c) => rappel.current(c));
  }, [ctx, cle]);
}

export const useRealtimeContext = () => {
  const ctx = useContext(RealtimeContext);
  if (!ctx) throw new Error('useRealtimeContext doit être utilisé dans RealtimeProvider');
  return ctx;
};
